import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, FlexboxGrid } from 'rsuite';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectCurrentStep,
  selectCurrentFlow,
} from '../../store/slices/flow/flowSelectors';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { clearApplication } from '../../store/slices/apply/applySlice';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { AppDispatch } from '../../store/store';

const CONFIRMATION_PATH_BY_FLOW: Partial<Record<FLOWS_ENUM, string>> = {
  [FLOWS_ENUM.APPLY_LICENSE]: '/apply-confirmation',
  [FLOWS_ENUM.RENEW_LICENSE]: '/renew-confirmation',
  [FLOWS_ENUM.PAY_TOT]: '/tax-confirm-and-sign',
  [FLOWS_ENUM.BULK_RENEWAL]: '/bulk-renew-confirmation',
};

const PaymentRedirect = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [hideNextButton, setHideNextButton] = useState(true);

  const selectedCurrentStep = useSelector(selectCurrentStep);
  const currentFlow = useSelector(selectCurrentFlow);

  // Push a duplicate history entry on mount so the first browser back stays on
  // this page and fires popstate instead of leaving immediately.
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      // Re-push to trap repeated back presses before we navigate away.
      window.history.pushState(null, '', window.location.href);
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: false })
      );
      dispatch(setCurrentStep(selectedCurrentStep - 1));
      if (currentFlow === FLOWS_ENUM.APPLY_LICENSE) {
        dispatch(clearApplication());
      }
      if (currentFlow) {
        placeNavigate(CONFIRMATION_PATH_BY_FLOW[currentFlow] ?? '');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentFlow, dispatch, placeNavigate, selectedCurrentStep]);

  const handleNext = () => {
    placeNavigate(`/dashboard`);
  };

  const handleBack = () => {
    dispatch(
      setStepCompletedStatus({ stepNum: selectedCurrentStep, status: false })
    );
    dispatch(setCurrentStep(selectedCurrentStep - 1));
    if (currentFlow === FLOWS_ENUM.APPLY_LICENSE) {
      dispatch(clearApplication());
    }
    if (currentFlow) {
      placeNavigate(CONFIRMATION_PATH_BY_FLOW[currentFlow] ?? '');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setHideNextButton(false);
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, []);

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={currentFlow} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Payment"
            onNext={handleNext as unknown as () => Promise<boolean>}
            onBack={handleBack as unknown as () => Promise<boolean>}
            nextButtonText="Return to Dashboard"
            backButtonText="Back"
            hideNextButton={hideNextButton}
          >
            <p>
              A new browser window has been opened which will allow you to
              complete payment through an external payment provider.
            </p>
            <p>Please continue with your payment in this new browser window.</p>
            <p>
              Once your payment is completed please return to this browser
              window.
            </p>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default PaymentRedirect;
